import { LitElement, html } from 'lit';
import { property, query } from 'lit/decorators.js';
import { styles } from '../view/styles';

export class GzRadioWebComponent extends LitElement {
  @property({ type: String, attribute: true, reflect: true })
  label: string;

  @property({ type: String, attribute: true, reflect: true })
  name: string;

  /**
   * A property that determines whether the label text should wrap onto a new line.
   * If set to `true`, the label text will wrap onto a new line when it exceeds the width of its container.
   * If set to `false`, the label text will stay on a single line, regardless of its length.
   * @type {boolean}
   */
  @property({ type: Boolean, attribute: true, reflect: true })
  wrapped: false;

  @property({ type: String, attribute: true, reflect: true })
  value: string | number;

  /**
   * Set property to `true` if the radio button should be checked.
   */
  @property({ type: Boolean, reflect: true })
  checked = false;

  /**
   * Set property to `true` if the radio button should be disabled.
   */
  @property({ type: Boolean, reflect: true })
  disabled = false;

  /**
   * Set property to `true` if the radio button should be highlighted (and input hidden).
   */
  @property({ type: Boolean, reflect: true })
  highlightOnCheck = false;

  @property({ type: Number })
  tabIndex = 0;

  @query('input')
  protected radio!: HTMLInputElement;

  static get styles() {
    return styles;
  }

  constructor() {
    super();
    this.addEventListener('click', this.clickHandler);
  }

  private clickHandler(e: Event) {
    if (this.disabled) {
      e.stopImmediatePropagation();
    }
  }

  public focus() {
    this.radio?.focus();
  }

  render() {
    return html`
      <div
        class="radio-button ${this.checked ? 'checked' : ''} ${this.disabled ? 'disabled' : ''} "
        ?highlightOnCheck="${this.highlightOnCheck}"
      >
        <input
          type="radio"
          class="radio-button--input"
          ?checked="${this.checked}"
          .value="${this.value}"
          .name="${this.name}"
          ?disabled="${this.disabled}"
          tabindex=${this.tabIndex}
          @change="${this.handleChange}"
        />
        <span class="radio-button--appearance"></span>
        <span class="radio-button--text" part="radio-label">${this.label}</span>
      </div>
    `;
  }

  handleChange(e: Event) {
    this.checked = (e.target as HTMLInputElement).checked;

    /**
     * Emit the change event to the ancestor
     */
    const changeEvent = new CustomEvent('change', {
      detail: {
        sourceEvent: e,
      },
      bubbles: true,
      cancelable: e.cancelable,
      composed: true,
    });
    this.dispatchEvent(changeEvent);
  }
}
